// /app/frontend/src/components/workflow/WorkflowUIComponents.jsx
// Reusable UI components for all workflow pages (DSR, Vendor, Breach, DPIA, etc.)

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { X, ChevronDown, ChevronRight, CheckCircle2 } from 'lucide-react';
import { cn } from '../../lib/utils';

// ==================== TAG INPUT ====================
// Used in: Breach (systems_affected), can be reused elsewhere
export function TagInput({ tags = [], onChange, placeholder = "Type and press Enter..." }) {
  const [input, setInput] = useState('');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault();
      if (!tags.includes(input.trim())) {
        onChange([...tags, input.trim()]);
      }
      setInput('');
    }
  };

  const removeTag = (tag) => {
    onChange(tags.filter(t => t !== tag));
  };

  return (
    <div className="space-y-2">
      <Input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
      />
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="gap-1 pr-1">
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== CHECKBOX GRID ====================
// Used in: Breach (containment measures), DSR (departments), Vendor (countries)
export function CheckboxGrid({ options, selected = [], onChange, columns = 2, renderLabel }) {
  const toggle = (opt) => {
    const value = typeof opt === 'object' ? opt.value : opt;
    if (selected.includes(value)) {
      onChange(selected.filter(s => s !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const getValue = (opt) => typeof opt === 'object' ? opt.value : opt;
  const getLabel = (opt) => {
    if (renderLabel) return renderLabel(opt);
    return typeof opt === 'object' ? opt.label : opt;
  };

  return (
    <div className={cn("grid grid-cols-1 gap-2", `sm:grid-cols-${columns}`)}>
      {options.map((opt) => {
        const value = getValue(opt);
        return (
          <label
            key={value}
            className={cn(
              "flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-all",
              selected.includes(value)
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50"
            )}
          >
            <Checkbox
              checked={selected.includes(value)}
              onCheckedChange={() => toggle(opt)}
            />
            <span className="text-sm">{getLabel(opt)}</span>
          </label>
        );
      })}
    </div>
  );
}

// ==================== CHIP GRID ====================
// Used in: Breach (who_affected), can be reused for multi-select chips
export function ChipGrid({ options, selected = [], onChange }) {
  const toggle = (opt) => {
    const value = typeof opt === 'object' ? opt.value : opt;
    if (selected.includes(value)) {
      onChange(selected.filter(s => s !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const getValue = (opt) => typeof opt === 'object' ? opt.value : opt;
  const getLabel = (opt) => typeof opt === 'object' ? opt.label : opt;

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const value = getValue(opt);
        const label = getLabel(opt);
        return (
          <button
            key={value}
            type="button"
            onClick={() => toggle(opt)}
            className={cn(
              "px-3 py-1.5 rounded-full text-sm border transition-all",
              selected.includes(value)
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background border-border hover:border-primary"
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ==================== SELECTION CARD ====================
// Used in: DSR (request types), Breach (breach types), Vendor (vendor types)
export function SelectionCard({ 
  selected, 
  onClick, 
  icon: Icon, 
  title, 
  subtitle, 
  testId,
  variant = 'default' // 'default' | 'danger' | 'success'
}) {
  const variantStyles = {
    default: selected 
      ? "border-primary bg-primary/5 ring-1 ring-primary" 
      : "border-border hover:border-primary/50",
    danger: selected 
      ? "border-red-500 bg-red-50 dark:bg-red-950/30 ring-2 ring-red-500" 
      : "border-border hover:border-red-300 hover:bg-red-50/50 dark:hover:bg-red-950/20",
    success: selected 
      ? "border-green-500 bg-green-50 dark:bg-green-950/30 ring-2 ring-green-500" 
      : "border-border hover:border-green-300 hover:bg-green-50/50 dark:hover:bg-green-950/20",
  };

  const iconColors = {
    default: "text-primary",
    danger: "text-red-600 dark:text-red-400",
    success: "text-green-600 dark:text-green-400",
  };

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "p-4 border rounded-xl text-left transition-all relative",
        variantStyles[variant]
      )}
    >
      {selected && <CheckCircle2 className={cn("absolute top-2 right-2 h-5 w-5", iconColors[variant])} />}
      {Icon && <Icon className={cn("h-6 w-6 mb-2", iconColors[variant])} />}
      <div className="font-medium text-sm">{title}</div>
      {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
    </button>
  );
}

// ==================== YES/NO BUTTON GROUP ====================
// Used in: DSR (identity verified), Breach (data search completed)
export function YesNoButtonGroup({ 
  value, 
  onChange, 
  yesLabel = "Yes", 
  noLabel = "No",
  yesIcon: YesIcon,
  noIcon: NoIcon,
  testIdPrefix = "yn"
}) {
  return (
    <div className="flex gap-2">
      <Button
        type="button"
        variant={value === true ? "default" : "outline"}
        size="sm"
        onClick={() => onChange(true)}
        data-testid={`${testIdPrefix}-yes`}
      >
        {YesIcon && <YesIcon className="h-4 w-4 mr-1" />}
        {yesLabel}
      </Button>
      <Button
        type="button"
        variant={value === false ? "default" : "outline"}
        size="sm"
        onClick={() => onChange(false)}
        data-testid={`${testIdPrefix}-no`}
      >
        {NoIcon && <NoIcon className="h-4 w-4 mr-1" />}
        {noLabel}
      </Button>
    </div>
  );
}

// ==================== POINTS BAR ====================
// Used in: Breach (scoring summary)
export function PointsBar({ label, current, max, color = "bg-primary" }) {
  const percentage = max > 0 ? Math.min(100, (current / max) * 100) : 0;
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono font-medium">{current}/{max}</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={cn("h-full rounded-full transition-all", color)}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

// ==================== ACCORDION CHECKBOX GROUP ====================
// Used in: Breach (root causes)
export function AccordionCheckboxGroup({ categories, selected = [], onChange }) {
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (name) => {
    setExpanded(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const toggleItem = (item) => {
    if (selected.includes(item)) {
      onChange(selected.filter(s => s !== item));
    } else {
      onChange([...selected, item]);
    }
  };

  return (
    <div className="space-y-2">
      {categories.map((cat) => {
        const isOpen = expanded[cat.name];
        const selectedCount = cat.items.filter(i => selected.includes(i)).length;
        
        return (
          <div key={cat.name} className="border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => toggleExpand(cat.name)}
              className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className={cn("w-3 h-3 rounded-full", cat.color)} />
                <span className="font-medium text-sm">{cat.name}</span>
                {selectedCount > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {selectedCount}
                  </Badge>
                )}
              </div>
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {isOpen && (
              <div className="border-t p-3 space-y-2 bg-muted/20">
                {cat.items.map((item) => (
                  <label
                    key={item}
                    className={cn(
                      "flex items-center gap-2 p-2 rounded cursor-pointer transition-colors",
                      selected.includes(item) ? "bg-primary/10" : "hover:bg-muted"
                    )}
                  >
                    <Checkbox
                      checked={selected.includes(item)}
                      onCheckedChange={() => toggleItem(item)}
                    />
                    <span className="text-sm">{item}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ==================== TAB CHECKBOX GROUP ====================
// Used in: Breach Step 3 (root causes) - horizontal tabs with checkboxes below
// Shows indicator dots on tabs where items are selected
export function TabCheckboxGroup({ categories, selected = [], onChange }) {
  const [activeTab, setActiveTab] = useState(categories[0]?.name || '');

  const toggleItem = (item) => {
    if (selected.includes(item)) {
      onChange(selected.filter(s => s !== item));
    } else {
      onChange([...selected, item]);
    }
  };

  const activeCategory = categories.find(cat => cat.name === activeTab);
  const selectedCount = activeCategory ? activeCategory.items.filter(i => selected.includes(i)).length : 0;

  return (
    <div className="space-y-4">
      {/* Tab Navigation - Horizontal */}
      <div className="flex gap-2 overflow-x-auto pb-2 border-b">
        {categories.map((cat) => {
          const catSelectedCount = cat.items.filter(i => selected.includes(i)).length;
          const isActive = cat.name === activeTab;

          return (
            <button
              key={cat.name}
              type="button"
              onClick={() => setActiveTab(cat.name)}
              className={cn(
                "px-4 py-2 text-sm font-medium whitespace-nowrap rounded-t-lg border-b-2 transition-all flex items-center gap-2",
                isActive
                  ? "border-primary text-primary bg-primary/5"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <span>{cat.name}</span>
              {/* Indicator dot if items selected in this tab */}
              {catSelectedCount > 0 && (
                <span className={cn("w-2 h-2 rounded-full", cat.color)} />
              )}
            </button>
          );
        })}
      </div>

      {/* Checkboxes for Active Tab */}
      {activeCategory && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-3">
            <div className={cn("w-3 h-3 rounded-full", activeCategory.color)} />
            <span className="font-medium text-sm">{activeCategory.name}</span>
            {selectedCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {selectedCount}/{activeCategory.items.length}
              </Badge>
            )}
          </div>

          <div className="space-y-2">
            {activeCategory.items.map((item) => (
              <label
                key={item}
                className={cn(
                  "flex items-center gap-2 p-2 rounded cursor-pointer transition-colors",
                  selected.includes(item) ? "bg-primary/10" : "hover:bg-muted"
                )}
              >
                <Checkbox
                  checked={selected.includes(item)}
                  onCheckedChange={() => toggleItem(item)}
                />
                <span className="text-sm">{item}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== SCORED ACCORDION GROUP ====================
// Used in: Breach (data categories, impact areas)
export function ScoredAccordionGroup({ categories, selected = [], onChange }) {
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (name) => {
    setExpanded(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const toggleItem = (item) => {
    if (selected.includes(item.name)) {
      onChange(selected.filter(s => s !== item.name));
    } else {
      onChange([...selected, item.name]);
    }
  };

  return (
    <div className="space-y-2">
      {categories.map((cat) => {
        const isOpen = expanded[cat.name];
        const selectedItems = cat.items.filter(i => selected.includes(i.name));
        const totalPoints = selectedItems.reduce((sum, i) => sum + (i.points || 0), 0);
        
        return (
          <div key={cat.name} className="border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => toggleExpand(cat.name)}
              className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="font-medium text-sm">{cat.name}</span>
                {selectedItems.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {selectedItems.length} selected · {totalPoints} pts
                  </Badge>
                )}
              </div>
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {isOpen && (
              <div className="border-t p-3 space-y-2 bg-muted/20">
                {cat.items.map((item) => (
                  <label
                    key={item.name}
                    className={cn(
                      "flex items-center justify-between gap-2 p-2 rounded cursor-pointer transition-colors",
                      selected.includes(item.name) ? "bg-primary/10" : "hover:bg-muted"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selected.includes(item.name)}
                        onCheckedChange={() => toggleItem(item)}
                      />
                      <span className="text-sm">{item.name}</span>
                    </div>
                    <span className="text-xs font-mono text-muted-foreground">
                      +{item.points}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ==================== RISK BAND BADGE ====================
// Used in: Breach risk display
export function RiskBandBadge({ score, band }) {
  const colorMap = {
    minimal: 'bg-green-500',
    low: 'bg-yellow-500',
    medium: 'bg-orange-500',
    high: 'bg-red-500',
    critical: 'bg-purple-500',
  };

  return (
    <Badge className={cn("text-white", colorMap[band?.toLowerCase()] || 'bg-gray-500')}>
      {score} - {band}
    </Badge>
  );
}

// ==================== OPTION BUTTON GRID ====================
// Used in: DSR (received via), Vendor (contract status)
export function OptionButtonGrid({ 
  options, 
  value, 
  onChange, 
  columns = 4,
  testIdPrefix = "opt"
}) {
  return (
    <div className={cn("grid gap-2", `grid-cols-2 sm:grid-cols-${columns}`)}>
      {options.map((opt) => {
        const optValue = typeof opt === 'object' ? opt.value : opt;
        const optLabel = typeof opt === 'object' ? opt.label : opt;
        return (
          <button
            key={optValue}
            type="button"
            data-testid={`${testIdPrefix}-${optValue.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={() => onChange(optValue)}
            className={cn(
              "p-3 border rounded-lg text-sm text-center transition-all",
              value === optValue
                ? "border-primary bg-primary/10 text-primary font-medium"
                : "border-border hover:border-primary/50"
            )}
          >
            {optLabel}
          </button>
        );
      })}
    </div>
  );
}

// ==================== CLAUSE CHECKLIST ====================
// Used in: Vendor (DPA clauses)
export function ClauseChecklist({ 
  clauses, 
  values = {}, 
  onChange,
  statusOptions = [
    { value: 'not_reviewed', label: 'Not Reviewed' },
    { value: 'present', label: 'Present' },
    { value: 'missing', label: 'Missing' },
    { value: 'inadequate', label: 'Inadequate' },
  ]
}) {
  return (
    <div className="space-y-3">
      {clauses.map(clause => (
        <div key={clause.id} className="flex items-center justify-between p-3 border rounded-lg">
          <div className="flex-1">
            <p className="text-sm font-medium">
              {clause.label}
              {clause.required && <span className="text-red-500 ml-1">*</span>}
            </p>
          </div>
          <Select
            value={values[clause.id] || "not_reviewed"}
            onValueChange={(val) => onChange(clause.id, val)}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}

// ==================== CONTACT FORM CARD ====================
// Used in: Vendor (primary, DPO, security contacts)
export function ContactFormCard({ 
  title, 
  icon: Icon, 
  contact = {}, 
  onChange,
  fields = ['name', 'email', 'phone']
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {Icon && <Icon className="h-5 w-5 text-primary" />}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className={cn("grid gap-4", `grid-cols-1 md:grid-cols-${fields.length}`)}>
          {fields.includes('name') && (
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={contact.name || ''}
                onChange={(e) => onChange('name', e.target.value)}
                placeholder="Contact name"
              />
            </div>
          )}
          {fields.includes('email') && (
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={contact.email || ''}
                onChange={(e) => onChange('email', e.target.value)}
                placeholder="Email address"
              />
            </div>
          )}
          {fields.includes('phone') && (
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={contact.phone || ''}
                onChange={(e) => onChange('phone', e.target.value)}
                placeholder="Phone number"
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== SUMMARY CARD ====================
// Used in: DSR (review tab), Vendor (review tab)
export function SummaryCard({ label, value, subValue, className }) {
  return (
    <div className={cn("p-4 border rounded-lg", className)}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium">{value || '-'}</p>
      {subValue && <p className="text-sm text-muted-foreground">{subValue}</p>}
    </div>
  );
}

// ==================== SUMMARY BADGE CARD ====================
// Used in: DSR (review tab status badges)
export function SummaryBadgeCard({ label, value, badgeVariant = "secondary" }) {
  return (
    <div className="p-4 border rounded-lg text-center">
      <p className="text-xs text-muted-foreground">{label}</p>
      <Badge variant={badgeVariant}>
        {value || 'Pending'}
      </Badge>
    </div>
  );
}

// ==================== WORKFLOW NAV BUTTONS ====================
// Used in: All workflow pages (bottom navigation)
export function WorkflowNavButtons({
  onPrevious,
  onNext,
  onCancel,
  onSaveDraft,
  onSubmit,
  isFirstStep,
  isLastStep,
  saving,
  cancelPath,
  submitLabel = "Save & Close",
  draftLabel = "Save as Draft"
}) {
  return (
    <div className="flex items-center justify-between pt-4 border-t">
      <Button
        variant="outline"
        onClick={onPrevious}
        disabled={isFirstStep}
      >
        Previous
      </Button>

      <div className="flex gap-2">
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        {onSaveDraft && (
          <Button variant="outline" onClick={onSaveDraft} disabled={saving}>
            {draftLabel}
          </Button>
        )}
        {isLastStep ? (
          <Button onClick={onSubmit} disabled={saving}>
            {saving ? 'Saving...' : submitLabel}
          </Button>
        ) : (
          <Button onClick={onNext}>
            Next
          </Button>
        )}
      </div>
    </div>
  );
}
